import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import moment from "moment";
import { toast } from "react-toastify";
import {
  LuBuilding2,
  LuCalendar,
  LuHome,
  LuIndianRupee,
  LuMapPin,
  LuPackage,
  LuPhone,
  LuWallet,
} from "react-icons/lu";

import AppLayout from "../../components/layout/AppLayout";
import Breadcrumb from "../../components/layout/Breadcrumb";
import Badge from "../../components/ui/Badge";
import Button from "../../components/ui/Button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/Table";
import {
  LoadingState,
  TableEmptyRow,
} from "../../components/ui/TableStateRow";
import cx from "../../components/ui/cx";
import {
  actionBarEndClass,
  cardClass,
  cardContentClass,
  cardContentFlushClass,
  cardHeaderClass,
  cardTitleClass,
  moneyCellClass,
  pageTitleClass,
  tableHeadCellClass,
} from "../../components/ui/styles";
import { useAppSelector } from "../../app/hooks";
import { getSizeUnit } from "../../features/appSettings/appSettingsSlice";
import { getLoggingUser } from "../../features/loggingUser/loggingUserSlice";
import { GET } from "../../services/axios.service";
import { compareSizes, sizeLabel } from "../../static/itemSize";
import { partyTypeBadgeClass, partyTypeLabel } from "../../static/partyType";
import {
  compareCodes,
  formatRate,
  partyProcessTypes,
  rateField,
  rateLabel,
} from "../../static/processRate";
import { hasPermission, permission } from "../../static/permission";
import { route } from "../../static/route";
import { toastStyles } from "../../static/styles";
import { BreadcrumbType } from "../../types/common";
import { PartiesProps, PartyRateProps } from "../../types/parties";

const breadcrumbItems: BreadcrumbType[] = [
  { label: "હોમ", href: route.dashboard },
  { label: "પાર્ટીઓ", href: route.parties },
  { label: "વિગતો" },
];

const formatDateTime = (value: string) =>
  moment(value).format("DD-MM-YYYY hh:mm A");

const typeBadges = (types: string[]) =>
  types.map((type) => (
    <Badge key={type} variant="none" className={partyTypeBadgeClass(type)}>
      {partyTypeLabel(type)}
    </Badge>
  ));

const statusBadge = (status: number) =>
  status === 1 ? (
    <Badge
      variant="none"
      className="bg-green-100 text-green-800 hover:bg-green-100"
    >
      સક્રિય
    </Badge>
  ) : (
    <Badge variant="none" className="bg-red-100 text-red-800 hover:bg-red-100">
      નિષ્ક્રિય
    </Badge>
  );

const infoLabelClass = cx(tableHeadCellClass, "text-sm");

const infoIconClass = "h-4 w-4 mt-1 flex-shrink-0 text-gray-400";

// Each main item in code order, followed by its sub items in the order the item page shows them. An item's sizes
// follow its own rate, in reading order (12.5 before 94 before 120).
const sortRates = (rates: PartyRateProps[]) => {
  const mainCode = (rate: PartyRateProps) =>
    rate.item.parent?.name ?? rate.item.name;

  return [...rates].sort(
    (a, b) =>
      compareCodes(mainCode(a), mainCode(b)) ||
      Number(a.item.parent_id !== null) - Number(b.item.parent_id !== null) ||
      a.item_id - b.item_id ||
      Number(a.size_id !== null) - Number(b.size_id !== null) ||
      compareSizes(a.size?.name ?? "", b.size?.name ?? "")
  );
};

const PartyDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const loggingUser = useAppSelector(getLoggingUser);
  const sizeUnit = useAppSelector(getSizeUnit);

  const canPayments = hasPermission(
    loggingUser?.permissions,
    permission.paymentsRead
  );
  const canItems = hasPermission(
    loggingUser?.permissions,
    permission.partiesItems
  );

  const [party, setParty] = useState<PartiesProps | null>(null);
  const [rates, setRates] = useState<PartyRateProps[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const rateTypes = partyProcessTypes(party?.type);
  const showRates = canItems && rateTypes.length > 0;
  // The size column only shows once a rate is for a size.
  const showSizes = rates.some((rate) => rate.size_id !== null);

  useEffect(() => {
    const fetchParty = async () => {
      setIsLoading(true);
      try {
        // The rates need their own permission, so they are only asked for with it.
        const [response, ratesResponse]: any[] = await Promise.all([
          GET(`/v1/admin/parties/${id}`, {}),
          canItems ? GET(`/v1/admin/parties/${id}/items`, {}) : null,
        ]);
        if (response?.data?.status === "success") {
          setParty(response.data.data);
          setRates(
            ratesResponse?.data?.status === "success"
              ? ratesResponse.data.data.party_items || []
              : []
          );
        } else {
          setParty(null);
          toast.error(
            response?.data?.message ||
              "કંઈક ખોટું ગયું, કૃપા કરીને ફરીથી પ્રયત્ન કરો.",
            { ...(toastStyles as any) }
          );
        }
      } catch (error: any) {
        setParty(null);
        toast.error(
          error?.data?.message ||
            "કંઈક ખોટું ગયું, કૃપા કરીને ફરીથી પ્રયત્ન કરો.",
          { ...(toastStyles as any) }
        );
      } finally {
        setIsLoading(false);
      }
    };

    if (id) {
      fetchParty();
    }
  }, [id, canItems]);

  return (
    <AppLayout>
      <div className="mb-2 md:mb-0">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      <div className="flex items-center mb-4 md:mb-6 mt-2">
        <h1 className={pageTitleClass}>પાર્ટી ની વિગતો</h1>
      </div>

      {isLoading ? (
        <div className={cx(cardClass, "p-4 text-sm")}>
          <LoadingState />
        </div>
      ) : !party ? (
        <div className={cx(cardClass, "p-8 text-center text-gray-500")}>
          પાર્ટી ની વિગતો મળી નથી
        </div>
      ) : (
        <div className="space-y-4 md:space-y-6">
          {(canPayments || showRates) && (
            <div className={actionBarEndClass}>
              <div className="grid w-full grid-cols-1 gap-2 sm:flex sm:w-auto">
                {showRates && (
                  <Link
                    to={route.partyRates.replace(
                      ":id",
                      party.id as unknown as string
                    )}
                  >
                    <Button variant="outline" className="w-full">
                      <LuIndianRupee className="h-4 w-4" />
                      ભાવ
                    </Button>
                  </Link>
                )}

                {canPayments && (
                  <Link
                    to={route.payments.replace(
                      ":id",
                      party.id as unknown as string
                    )}
                  >
                    <Button variant="primary" className="w-full">
                      <LuWallet className="h-4 w-4" />
                      પેમેન્ટ
                    </Button>
                  </Link>
                )}
              </div>
            </div>
          )}

          <div className={cardClass}>
            <div className={cardHeaderClass}>
              <div className={`${cardTitleClass} text-lg`}>
                {party.firm_name || "-"}
              </div>
            </div>

            <div className={cardContentClass}>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                <div className="flex items-start gap-2">
                  <LuBuilding2 className={infoIconClass} />
                  <div className="min-w-0">
                    <div className={infoLabelClass}>નામ</div>
                    <div className="font-medium break-words">
                      {party.name || "-"}
                    </div>
                  </div>
                </div>

                <div className="flex items-start gap-2">
                  <LuPhone className={infoIconClass} />
                  <div className="min-w-0">
                    <div className={infoLabelClass}>ફોન</div>
                    <div className="font-medium">{party.phone || "-"}</div>
                    {party.wp_phone && (
                      <div className="text-gray-500">
                        વોટ્સએપ: {party.wp_phone}
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex items-start gap-2">
                  <LuMapPin className={infoIconClass} />
                  <div className="min-w-0">
                    <div className={infoLabelClass}>સરનામું</div>
                    <div className="font-medium break-words">
                      {party.address || "-"}
                    </div>
                  </div>
                </div>

                <div className="flex items-start gap-2">
                  <LuPackage className={infoIconClass} />
                  <div className="min-w-0">
                    <div className={infoLabelClass}>પ્રકાર</div>
                    <div className="flex flex-wrap gap-1">
                      {party.type && party.type.length > 0
                        ? typeBadges(party.type)
                        : "-"}
                    </div>
                  </div>
                </div>

                <div className="flex items-start gap-2">
                  <LuHome className={infoIconClass} />
                  <div className="min-w-0">
                    <div className={infoLabelClass}>ઇનહાઉસ</div>
                    <div className="font-medium">
                      {party.is_inhouse === 1 ? "હા" : "ના"}
                    </div>
                  </div>
                </div>

                <div className="flex items-start gap-2">
                  <LuPackage className={infoIconClass} />
                  <div className="min-w-0">
                    <div className={infoLabelClass}>સ્ટેટસ</div>
                    <div>{statusBadge(party.status)}</div>
                  </div>
                </div>

                <div className="flex items-start gap-2">
                  <LuCalendar className={infoIconClass} />
                  <div className="min-w-0">
                    <div className={infoLabelClass}>બનાવ્યા તારીખ</div>
                    <div className="font-medium">
                      {formatDateTime(party.created_at)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {showRates && (
            <div className={cardClass}>
              <div className={cardHeaderClass}>
                <div className={`${cardTitleClass} text-lg`}>આઈટમ ભાવ</div>
              </div>

              <div className={cardContentFlushClass}>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className={tableHeadCellClass}>ક્રમ</TableHead>
                        <TableHead className={tableHeadCellClass}>
                          આઈટમ
                        </TableHead>
                        {showSizes && (
                          <TableHead className={tableHeadCellClass}>
                            સાઈઝ
                          </TableHead>
                        )}
                        {rateTypes.map((type) => (
                          <TableHead
                            key={type.value}
                            className={cx(tableHeadCellClass, "text-right")}
                          >
                            {rateLabel(type)}
                          </TableHead>
                        ))}
                      </TableRow>
                    </TableHeader>

                    {rates.length === 0 ? (
                      <TableEmptyRow
                        colSpan={2 + Number(showSizes) + rateTypes.length}
                        label="ભાવ ની વિગતો મળી નથી"
                      />
                    ) : (
                      <TableBody>
                        {sortRates(rates).map((rate, index) => (
                          <TableRow
                            key={`${rate.item_id}-${rate.size_id ?? ""}`}
                          >
                            <TableCell className="font-medium">
                              {index + 1}
                            </TableCell>
                            <TableCell>
                              <div
                                className={cx(
                                  "font-medium",
                                  rate.item.parent && "pl-4"
                                )}
                              >
                                {rate.item.parent && "↳ "}
                                {rate.item.name}
                              </div>
                              {rate.item.parent && (
                                <div className="pl-4 text-xs text-gray-500">
                                  મુખ્ય આઈટમ: {rate.item.parent.name}
                                </div>
                              )}
                              {rate.stale && (
                                <div
                                  className={cx(
                                    "text-xs text-orange-700",
                                    rate.item.parent && "pl-4"
                                  )}
                                >
                                  સાઈઝ વગરનો જૂનો ભાવ
                                </div>
                              )}
                            </TableCell>
                            {showSizes && (
                              <TableCell className="font-medium">
                                {rate.size
                                  ? sizeLabel(rate.size.name, sizeUnit)
                                  : "-"}
                              </TableCell>
                            )}
                            {rateTypes.map((type) => (
                              <TableCell
                                key={type.value}
                                className={moneyCellClass}
                              >
                                {formatRate(rate[rateField(type.value)])}
                              </TableCell>
                            ))}
                          </TableRow>
                        ))}
                      </TableBody>
                    )}
                  </Table>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </AppLayout>
  );
};
export default PartyDetailPage;
